import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Star } from 'lucide-react';
import { Badge } from '@/components/shared/Badge';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { formatRupees } from '@/lib/utils/format';
import { useListings } from '@/features/marketplace/hooks/useListings';
import type { Listing } from '@/features/marketplace/types';

const TAG_COLORS = [brand.teal, brand.chinar, brand.ice, brand.saffron] as const;

function tagColor(index: number): string {
  return TAG_COLORS[index % TAG_COLORS.length];
}

function imgSrc(listing: Listing): string {
  return listing.media_url ?? unsplash(photo.valley, { w: 700 });
}

export function StaysGrid() {
  const { data: stays = [], isLoading } = useListings({ type: 'hotel', per_page: 4 });

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="Stays · 230 verified"
        kickerColor={brand.chinar}
        title={
          <>
            Sleep close to <em>where you&apos;re going.</em>
          </>
        }
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/stays">
              All stays <ArrowRight size={14} />
            </Link>
          </Button>
        }
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4"
      >
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <motion.div key={i} variants={fadeUp}>
                <GlassCard className="overflow-hidden p-0">
                  <div className="aspect-[4/3] animate-pulse bg-white/10" />
                  <div className="space-y-2 p-3.5">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-white/10" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-white/10" />
                    <div className="h-5 w-1/3 animate-pulse rounded bg-white/10" />
                  </div>
                </GlassCard>
              </motion.div>
            ))
          : stays.map((s, i) => (
              <motion.div key={s.id} variants={fadeUp}>
                <Link to={`/stays/${s.slug}`} className="block">
                  <GlassCard interactive className="overflow-hidden p-0">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={imgSrc(s)}
                        alt={s.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      {s.tags?.[0] && (
                        <div className="absolute left-2.5 top-2.5">
                          <Badge color={tagColor(i)}>{s.tags[0]}</Badge>
                        </div>
                      )}
                    </div>
                    <div className="p-3.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-display text-[15px] font-semibold leading-tight tracking-tight text-ink">
                          {s.name}
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-ink">
                          <Star size={10} fill={brand.warn} color={brand.warn} />
                          {s.rating}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] text-ink-dim">{s.location?.city}</div>
                      <div className="mt-2.5">
                        <span className="font-display text-[17px] font-bold text-ink">
                          {formatRupees(Math.round((s.price_paisa ?? 0) / 100))}
                        </span>
                        <span className="text-[11px] text-ink-mute"> /night</span>
                      </div>
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            ))}
      </motion.div>
    </section>
  );
}
